import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSpotlight } from '../contexts/SpotlightContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, ChevronRight, ChevronDown, CheckCircle2, Play,
  Users, Calendar, MessageSquare, FileText, Shield, CreditCard,
  Heart, Star, Award, BarChart3
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ROLE_GUIDES = {
  agency_admin: {
    title: 'Agency Admin Guide',
    description: 'Manage your care agency operations end-to-end',
    sections: [
      {
        title: 'Dashboard Overview',
        icon: BarChart3,
        steps: [
          { title: 'Stats at a Glance', text: 'Your dashboard shows total caregivers, clients, shifts, and pending shifts. These update in real-time as your team grows.', navigateTo: '/dashboard/admin', highlight: '[data-testid="stats-grid"]' },
          { title: 'Send Invitations', text: 'Use the "Invite User" button to invite caregivers, clients, or family members. Each gets a unique invite link with a 5-day expiry.', navigateTo: '/dashboard/admin', highlight: '[data-testid="invite-user-btn"]' },
          { title: 'Leaderboard', text: 'See your top-performing caregivers ranked by shifts completed, helping you recognize and reward your best staff.', navigateTo: '/dashboard/admin', highlight: '[data-testid="leaderboard-widget"]' },
        ]
      },
      {
        title: 'People Management',
        icon: Users,
        steps: [
          { title: 'Caregivers', text: 'View all caregivers, their staff numbers, compliance status, and assigned clients. Click any caregiver to see their full profile.', navigateTo: '/dashboard/admin/caregivers', highlight: '[data-testid="caregivers-list"]' },
          { title: 'Clients', text: 'Manage client profiles, care plans, and assigned caregivers. Each client has a dedicated folder for documents and care journals.', navigateTo: '/dashboard/admin/clients', highlight: '[data-testid="clients-list"]' },
          { title: 'Bulk Invite', text: 'Import multiple team members at once using the Bulk Invite feature. Upload a CSV or add them manually in batch.', navigateTo: '/bulk-invite', highlight: '[data-testid="bulk-invite-form"]' },
        ]
      },
      {
        title: 'Scheduling',
        icon: Calendar,
        steps: [
          { title: 'Create Shifts', text: 'Create shifts by selecting a client, assigning a caregiver, and setting the date/time. Use drag-and-drop on the calendar view.', navigateTo: '/dashboard/admin/shifts', highlight: '[data-testid="create-shift-btn"]' },
          { title: 'AI Scheduling', text: 'Let the AI suggest optimal shift assignments based on caregiver availability, location, and skills.', navigateTo: '/ai-scheduling', highlight: '[data-testid="ai-scheduling"]' },
          { title: 'Live Tracking', text: 'Monitor caregivers in real-time with GPS clock-in/out. See who is on shift and where they are on the map.', navigateTo: '/live-tracking', highlight: '[data-testid="live-tracking-map"]' },
          { title: 'Shift Swaps', text: 'Caregivers can request shift swaps which you approve or deny. All swaps are logged in the audit trail.', navigateTo: '/shift-swaps', highlight: '[data-testid="shift-swaps"]' },
          { title: 'Leave Requests', text: 'Review and approve time-off requests. The calendar automatically blocks out approved leave dates.', navigateTo: '/leave-requests', highlight: '[data-testid="leave-requests"]' },
        ]
      },
      {
        title: 'Communication',
        icon: MessageSquare,
        steps: [
          { title: 'Messaging', text: 'Send direct messages to any team member. Messages support file attachments and are encrypted end-to-end.', navigateTo: '/messaging', highlight: '[data-testid="messaging-container"]' },
          { title: 'Team Chat', text: 'Create group chat rooms for teams, departments, or project groups. Everyone in the room sees messages in real-time.', navigateTo: '/team-chat', highlight: '[data-testid="team-chat"]' },
          { title: 'Broadcasts', text: 'Send announcements to all staff or specific roles. Great for policy updates, emergency alerts, or celebrations.', navigateTo: '/broadcasts', highlight: '[data-testid="broadcasts"]' },
        ]
      },
      {
        title: 'Records & Compliance',
        icon: FileText,
        steps: [
          { title: 'Timesheets', text: 'View and approve caregiver timesheets. Export to CSV for payroll processing.', navigateTo: '/timesheets', highlight: '[data-testid="timesheets"]' },
          { title: 'Invoices', text: 'Create and manage invoices for clients. Supports multiple currencies and PDF export.', navigateTo: '/invoices', highlight: '[data-testid="invoices-list"]' },
          { title: 'Document Storage', text: 'Upload and organize documents. Set permissions so only authorized users can view sensitive files.', navigateTo: '/document-storage', highlight: '[data-testid="document-storage"]' },
          { title: 'Compliance', text: 'Track caregiver certifications, DBS checks, and training. Get automatic alerts before documents expire.', navigateTo: '/caregiver-compliance', highlight: '[data-testid="compliance-dashboard"]' },
        ]
      },
      {
        title: 'Billing & Subscription',
        icon: CreditCard,
        steps: [
          { title: 'View Your Plan', text: 'See your current subscription tier, active users, and renewal date on the Billing page.', navigateTo: '/subscription', highlight: '[data-testid="current-plan-card"]' },
          { title: 'Contract Plans', text: 'Choose from 12, 24, or 36-month contracts to get free months. Prices display in your local currency.', navigateTo: '/subscription', highlight: '[data-testid="contract-plans-section"]' },
          { title: 'Auto-Renewal', text: 'Contracts auto-renew by default. To cancel, provide 3 months notice before the contract end date.', navigateTo: '/subscription', highlight: '[data-testid="renewal-card"]' },
        ]
      },
    ]
  },
  ceo: {
    title: 'Principal Admin (CEO) Guide',
    description: 'Strategic oversight and governance of your care organization',
    sections: [
      {
        title: 'CEO Dashboard',
        icon: BarChart3,
        steps: [
          { title: 'Organization Overview', text: 'See key metrics including total staff, active shifts, compliance rates, and financial summary.', navigateTo: '/dashboard/ceo', highlight: '[data-testid="ceo-dashboard"]' },
          { title: 'Admin Delegation', text: 'Delegate specific admin functions to trusted team members without giving full admin access.', navigateTo: '/dashboard/ceo/admins', highlight: '[data-testid="delegation-section"]' },
          { title: 'Transfer Principal Role', text: 'If needed, transfer the CEO role to another team member. Type TRANSFER to confirm this irreversible action.', navigateTo: '/dashboard/ceo/admins', highlight: '[data-testid="transfer-principal-btn"]' },
        ]
      },
      {
        title: 'Staff Management',
        icon: Users,
        steps: [
          { title: 'View All Staff', text: 'See every member of your organization with their role, status, and key details.', navigateTo: '/dashboard/ceo/staff', highlight: '[data-testid="staff-list"]' },
          { title: 'Suspend/Reinstate', text: 'Temporarily suspend a staff member (they lose access but data is preserved) or reinstate them.', navigateTo: '/dashboard/ceo/staff', highlight: '[data-testid="staff-list"]' },
          { title: 'Remove Members', text: 'Permanently remove a user from your organization. This action is logged in the audit trail.', navigateTo: '/dashboard/ceo/staff', highlight: '[data-testid="staff-list"]' },
        ]
      },
      {
        title: 'Operations & Reporting',
        icon: Calendar,
        steps: [
          { title: 'Shift Oversight', text: 'View all shifts across the organization. Filter by caregiver, client, or date range.', navigateTo: '/dashboard/admin/shifts', highlight: '[data-testid="shifts-calendar"]' },
          { title: 'Analytics', text: 'Access detailed analytics on shift completion rates, caregiver utilization, and client satisfaction.', navigateTo: '/analytics', highlight: '[data-testid="analytics-dashboard"]' },
          { title: 'Incident Reports', text: 'Review all incident reports filed by caregivers. Track resolution status and follow-up actions.', navigateTo: '/incident-reports', highlight: '[data-testid="incident-reports"]' },
        ]
      },
      {
        title: 'Governance',
        icon: Shield,
        steps: [
          { title: 'Billing Control', text: 'Manage subscription, view payment history, and select contract plans with promotional free months.', navigateTo: '/subscription', highlight: '[data-testid="subscription-dashboard"]' },
          { title: 'Security Settings', text: 'Configure MFA requirements, session timeouts, and IP restrictions for your organization.', navigateTo: '/security-settings', highlight: '[data-testid="security-settings"]' },
          { title: 'Organization Profile', text: 'Update your agency name, address, logo, and public directory listing.', navigateTo: '/dashboard/ceo/organization', highlight: '[data-testid="org-profile"]' },
        ]
      },
    ]
  },
  caregiver: {
    title: 'Caregiver Guide',
    description: 'Everything you need to manage your shifts and care duties',
    sections: [
      {
        title: 'Your Dashboard',
        icon: BarChart3,
        steps: [
          { title: 'Shift Map', text: 'See your upcoming shifts on an interactive map showing client locations and travel routes.', navigateTo: '/dashboard/caregiver', highlight: '[data-testid="caregiver-dashboard"]' },
          { title: 'Today\'s Schedule', text: 'View your shifts for today with client details, times, and any special instructions.', navigateTo: '/dashboard/caregiver', highlight: '[data-testid="today-shifts"]' },
          { title: 'Quick Stats', text: 'Track your completed shifts, hours worked this week, and upcoming shifts at a glance.', navigateTo: '/dashboard/caregiver', highlight: '[data-testid="caregiver-stats"]' },
        ]
      },
      {
        title: 'Shifts',
        icon: Calendar,
        steps: [
          { title: 'My Shifts', text: 'View all your assigned shifts — past, present, and future. Click any shift for full details.', navigateTo: '/dashboard/caregiver/shifts', highlight: '[data-testid="my-shifts"]' },
          { title: 'Clock In/Out', text: 'Use GPS-verified clock in when you arrive at a client location. Clock out when your shift ends.', navigateTo: '/dashboard/caregiver/shifts', highlight: '[data-testid="clock-in-btn"]' },
          { title: 'Available Shifts', text: 'Browse open shifts you can pick up. Filter by location, date, or care level required.', navigateTo: '/dashboard/caregiver/available', highlight: '[data-testid="available-shifts"]' },
          { title: 'Swap Shifts', text: 'Request to swap a shift with another caregiver. Your admin will approve or deny the request.', navigateTo: '/shift-swaps', highlight: '[data-testid="shift-swaps"]' },
          { title: 'Request Leave', text: 'Submit time-off requests with dates and reason. Track approval status in real-time.', navigateTo: '/leave-requests', highlight: '[data-testid="leave-requests"]' },
        ]
      },
      {
        title: 'Daily Work',
        icon: Heart,
        steps: [
          { title: 'Tasks', text: 'View your task list for each shift. Check off items as you complete them.', navigateTo: '/my-tasks', highlight: '[data-testid="my-tasks"]' },
          { title: 'Care Journals', text: 'Write daily care notes for each client. Include observations, activities, and any concerns.', navigateTo: '/care-journals', highlight: '[data-testid="care-journals"]' },
          { title: 'Medications', text: 'View the Medication Administration Record (MAR) for your clients. Record when medications are given.', navigateTo: '/medications', highlight: '[data-testid="medications"]' },
          { title: 'Incident Reports', text: 'File an incident report immediately if anything unusual occurs. Attach photos if needed.', navigateTo: '/incident-reports', highlight: '[data-testid="incident-reports"]' },
        ]
      },
      {
        title: 'Career & Development',
        icon: Award,
        steps: [
          { title: 'Learning', text: 'Access training modules and courses assigned to you. Track your progress and earn certificates.', navigateTo: '/learning', highlight: '[data-testid="learning"]' },
          { title: 'Certifications', text: 'Upload and manage your professional certifications. Get reminders before they expire.', navigateTo: '/my-certifications', highlight: '[data-testid="certifications"]' },
          { title: 'Rewards', text: 'Earn points for completing shifts, training, and getting good reviews. Redeem for rewards.', navigateTo: '/my-rewards', highlight: '[data-testid="rewards"]' },
          { title: 'Performance Reviews', text: 'View feedback from your supervisors and track your improvement over time.', navigateTo: '/performance-reviews', highlight: '[data-testid="performance-reviews"]' },
        ]
      },
      {
        title: 'Records & Pay',
        icon: FileText,
        steps: [
          { title: 'Timesheets', text: 'View your timesheet with hours worked per shift. Flag any discrepancies for admin review.', navigateTo: '/timesheets', highlight: '[data-testid="timesheets"]' },
          { title: 'Payslips', text: 'Access your payslips and earnings history. Download PDF copies for your records.', navigateTo: '/payroll', highlight: '[data-testid="payroll"]' },
          { title: 'Documents', text: 'Upload and view your personal documents (contracts, DBS, training certificates).', navigateTo: '/document-storage', highlight: '[data-testid="document-storage"]' },
        ]
      },
    ]
  },
  client: {
    title: 'Client Guide',
    description: 'Stay informed about your care and connect with your care team',
    sections: [
      {
        title: 'Your Dashboard',
        icon: BarChart3,
        steps: [
          { title: 'Care Overview', text: 'See your upcoming appointments, assigned caregivers, and recent care notes all in one place.', navigateTo: '/dashboard/client', highlight: '[data-testid="client-dashboard"]' },
          { title: 'Quick Actions', text: 'Access your schedule, care plan, and messages with a single tap.', navigateTo: '/dashboard/client', highlight: '[data-testid="client-dashboard"]' },
        ]
      },
      {
        title: 'Your Care',
        icon: Heart,
        steps: [
          { title: 'My Schedule', text: 'View your upcoming care visits with dates, times, and which caregiver is coming.', navigateTo: '/dashboard/client/schedule', highlight: '[data-testid="client-schedule"]' },
          { title: 'Care Tracking', text: 'See real-time updates when your caregiver clocks in and out of your visits.', navigateTo: '/client/care-tracking', highlight: '[data-testid="care-tracking"]' },
          { title: 'My Caregivers', text: 'View profiles of all caregivers assigned to you, including their qualifications and specialties.', navigateTo: '/client/caregivers', highlight: '[data-testid="my-caregivers"]' },
          { title: 'Care Plan', text: 'Read your personalized care plan outlining your needs, preferences, and goals.', navigateTo: '/dashboard/client/care-plan', highlight: '[data-testid="care-plan"]' },
          { title: 'Care Journals', text: 'Read daily notes from your caregivers about each visit — what was done, observations, etc.', navigateTo: '/care-journals', highlight: '[data-testid="care-journals"]' },
          { title: 'Medications', text: 'View your medication schedule and confirm when doses are administered.', navigateTo: '/medications', highlight: '[data-testid="medications"]' },
        ]
      },
      {
        title: 'Feedback',
        icon: Star,
        steps: [
          { title: 'Rate Agency', text: 'Share your experience with the care agency. Your feedback helps improve services.', navigateTo: '/client/rate-agency', highlight: '[data-testid="rate-agency"]' },
          { title: 'Rate Caregiver', text: 'Rate individual caregivers after visits. Good ratings help recognize outstanding care.', navigateTo: '/client/rate-caregiver', highlight: '[data-testid="rate-caregiver"]' },
        ]
      },
    ]
  },
  family: {
    title: 'Family Member Guide',
    description: 'Stay connected with your loved one\'s care journey',
    sections: [
      {
        title: 'Family Dashboard',
        icon: BarChart3,
        steps: [
          { title: 'At a Glance', text: 'See your family member\'s upcoming visits, recent care notes, and caregiver information.', navigateTo: '/dashboard/family', highlight: '[data-testid="family-dashboard"]' },
          { title: 'Real-time Updates', text: 'Get notifications when caregivers clock in/out of visits with your loved one.', navigateTo: '/dashboard/family', highlight: '[data-testid="family-dashboard"]' },
        ]
      },
      {
        title: 'Care Monitoring',
        icon: Heart,
        steps: [
          { title: 'Schedule', text: 'View the full care schedule for your family member — who is coming and when.', navigateTo: '/dashboard/family/schedule', highlight: '[data-testid="family-schedule"]' },
          { title: 'Care Tracking', text: 'Follow live care sessions and see check-in/check-out times verified by GPS.', navigateTo: '/dashboard/family', highlight: '[data-testid="family-dashboard"]' },
          { title: 'Care Journals', text: 'Read detailed notes from each care visit. Understand what activities were done and how your loved one is doing.', navigateTo: '/care-journals', highlight: '[data-testid="care-journals"]' },
          { title: 'Medications', text: 'View the medication administration record. See when each medication was given.', navigateTo: '/medications', highlight: '[data-testid="medications"]' },
          { title: 'Care Reports', text: 'Access summary reports of care provided over days, weeks, or months. AI-generated summaries available.', navigateTo: '/dashboard/family/reports', highlight: '[data-testid="care-reports"]' },
        ]
      },
      {
        title: 'Communication',
        icon: MessageSquare,
        steps: [
          { title: 'Messages', text: 'Send messages to the care team. Ask questions, share concerns, or request changes.', navigateTo: '/dashboard/family', highlight: '[data-testid="family-dashboard"]' },
          { title: 'Photos', text: 'View photos shared by caregivers during visits — activities, meals, outings.', navigateTo: '/dashboard/family', highlight: '[data-testid="family-dashboard"]' },
        ]
      },
    ]
  },
  developer: {
    title: 'Platform Developer Guide',
    description: 'Platform administration, analytics, and company management',
    sections: [
      {
        title: 'Overview Dashboard',
        icon: BarChart3,
        steps: [
          { title: 'Platform Stats', text: 'View total users, organizations, revenue, and active users across the entire platform.', navigateTo: '/dashboard/developer', highlight: '[data-testid="developer-dashboard"]' },
          { title: 'Users by Role', text: 'See the distribution of users across all roles — admins, caregivers, clients, family, etc.', navigateTo: '/dashboard/developer', highlight: '[data-testid="developer-dashboard"]' },
        ]
      },
      {
        title: 'Revenue & Analytics',
        icon: CreditCard,
        steps: [
          { title: 'Revenue Dashboard', text: 'View monthly revenue with currency breakdown. Track total earnings and transaction counts.', navigateTo: '/dashboard/developer?tab=revenue', highlight: '[data-testid="revenue-tab"]' },
          { title: 'Usage Analytics', text: 'See daily/weekly active users, feature adoption rates, and peak usage times.', navigateTo: '/dashboard/developer?tab=usage', highlight: '[data-testid="usage-analytics-tab"]' },
          { title: 'Behavior & Churn', text: 'Monitor organization health, identify churn risks, and track feature engagement patterns.', navigateTo: '/dashboard/developer?tab=behavior', highlight: '[data-testid="behavior-tab"]' },
          { title: 'Onboarding Health', text: 'See health scores for each agency based on milestones hit within 7 days of registration.', navigateTo: '/dashboard/developer?tab=health', highlight: '[data-testid="health-tab"]' },
        ]
      },
      {
        title: 'Company Management',
        icon: Shield,
        steps: [
          { title: 'View Companies', text: 'See all registered agencies with CEO info, user counts, and subscription status.', navigateTo: '/dashboard/developer?tab=companies', highlight: '[data-testid="refresh-companies-btn"]' },
          { title: 'Suspend/Restore', text: 'Suspend agencies that violate terms or have payment issues. Restore them when resolved.', navigateTo: '/dashboard/developer?tab=companies', highlight: '[data-testid="refresh-companies-btn"]' },
          { title: 'Delete Companies', text: 'Permanently delete test or abandoned agencies with full cascade deletion of all data.', navigateTo: '/dashboard/developer?tab=companies', highlight: '[data-testid="refresh-companies-btn"]' },
          { title: 'Resend CEO Invites', text: 'Resend invitation emails to CEOs who haven\'t registered. Use bulk resend for all at once.', navigateTo: '/dashboard/developer?tab=companies', highlight: '[data-testid="bulk-resend-ceo-btn"]' },
          { title: 'Onboarding Timeline', text: 'Expand any company card to see their onboarding milestones — registration, first login, first shift, etc.', navigateTo: '/dashboard/developer?tab=companies', highlight: '[data-testid="refresh-companies-btn"]' },
        ]
      },
      {
        title: 'Promotions',
        icon: Star,
        steps: [
          { title: 'Concessions', text: 'Create one-time billing concessions for specific organizations (percentage or fixed discounts).', navigateTo: '/dashboard/developer?tab=promotions', highlight: '[data-testid="new-concession-btn"]' },
          { title: 'Promo Codes', text: 'Generate promotional codes with percentage discounts, free months, or fixed amounts off.', navigateTo: '/dashboard/developer?tab=promotions', highlight: '[data-testid="new-concession-btn"]' },
        ]
      },
    ]
  },
};

export default function TutorialGuide() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuto = searchParams.get('auto') === 'true';
  const role = user?.role || 'caregiver';
  const guide = ROLE_GUIDES[role] || ROLE_GUIDES.caregiver;
  const [expandedSections, setExpandedSections] = useState(isAuto ? { 0: true } : {});
  const [autoStarted, setAutoStarted] = useState(false);
  const { scheduleSpotlight, showSpotlight, advanceToNext } = useSpotlight();
  const [completedSteps, setCompletedSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`guide_progress_${role}`) || '{}'); }
    catch { return {}; }
  });

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const totalSteps = guide.sections.reduce((sum, s) => sum + s.steps.length, 0);
  const doneSteps = Object.values(completedSteps).filter(Boolean).length;
  const progressPercent = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const emailSentRef = useRef(localStorage.getItem(`onboarding_completed_${role}`) === 'true');

  const toggleStep = (sectionIdx, stepIdx) => {
    const key = `${sectionIdx}-${stepIdx}`;
    const updated = { ...completedSteps, [key]: !completedSteps[key] };
    setCompletedSteps(updated);
    localStorage.setItem(`guide_progress_${role}`, JSON.stringify(updated));
    // Check if 100% complete
    const newDone = Object.values(updated).filter(Boolean).length;
    if (newDone >= totalSteps && !emailSentRef.current) {
      emailSentRef.current = true;
      localStorage.setItem(`onboarding_completed_${role}`, 'true');
      axios.post(`${API_URL}/api/auth/onboarding-complete`, {}, { withCredentials: true })
        .then(() => {
          toast.success('Congratulations! You earned the Guide badge!');
          setUser(prev => prev ? { ...prev, guide_badge: true } : prev);
        })
        .catch(() => {});
    }
  };

  const getNextStep = useCallback((sIdx, stIdx) => {
    const section = guide.sections[sIdx];
    if (stIdx + 1 < section.steps.length) return { sIdx, stIdx: stIdx + 1 };
    if (sIdx + 1 < guide.sections.length) return { sIdx: sIdx + 1, stIdx: 0 };
    return null;
  }, [guide]);

  const buildSpotlightData = useCallback((sIdx, stIdx) => {
    const step = guide.sections[sIdx]?.steps[stIdx];
    if (!step) return null;
    const next = getNextStep(sIdx, stIdx);
    const nextStep = next ? guide.sections[next.sIdx]?.steps[next.stIdx] : null;
    // Build a serializable nextStep descriptor (no closures)
    let nextStepData = null;
    if (next && nextStep) {
      const afterNext = getNextStep(next.sIdx, next.stIdx);
      const afterNextStep = afterNext ? guide.sections[afterNext.sIdx]?.steps[afterNext.stIdx] : null;
      nextStepData = {
        target: nextStep.highlight,
        title: nextStep.title,
        text: nextStep.text,
        navigateTo: nextStep.navigateTo,
        hasNext: !!afterNext,
        nextStepAfter: afterNext && afterNextStep ? {
          target: afterNextStep.highlight,
          title: afterNextStep.title,
          text: afterNextStep.text,
          navigateTo: afterNextStep.navigateTo,
          hasNext: !!getNextStep(afterNext.sIdx, afterNext.stIdx),
        } : null,
      };
    }
    return {
      target: step.highlight,
      title: step.title,
      text: step.text,
      hasNext: !!next,
      nextStep: nextStepData,
    };
  }, [guide, getNextStep]);

  const startSpotlight = useCallback((sIdx, stIdx) => {
    const step = guide.sections[sIdx]?.steps[stIdx];
    if (!step) return;
    const data = buildSpotlightData(sIdx, stIdx);
    if (step.navigateTo) {
      scheduleSpotlight(data);
      navigate(step.navigateTo);
    } else {
      showSpotlight(data);
    }
  }, [guide, buildSpotlightData, scheduleSpotlight, showSpotlight, navigate]);

  // Auto-start spotlight on first login
  useEffect(() => {
    if (isAuto && !autoStarted && guide.sections.length > 0) {
      setAutoStarted(true);
      const timer = setTimeout(() => startSpotlight(0, 0), 800);
      return () => clearTimeout(timer);
    }
  }, [isAuto, autoStarted, guide, startSpotlight]);

  const markOnboardingComplete = () => {
    localStorage.setItem(`onboarding_completed_${role}`, 'true');
    navigate('/dashboard', { replace: true });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="tutorial-guide">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-[#00A6A0]" />
            {guide.title}
          </h1>
          <p className="text-muted-foreground mt-1">{guide.description}</p>
          {isAuto && (
            <div className="flex items-center gap-3 mt-3">
              <Button size="sm" variant="outline" className="text-xs border-muted-foreground text-muted-foreground"
                onClick={markOnboardingComplete} data-testid="skip-onboarding-btn">
                Skip & Go to Dashboard
              </Button>
              <span className="text-[10px] text-muted-foreground">You can return to this guide anytime from the sidebar</span>
            </div>
          )}
        </div>

        <Card className="glass-card border-border" data-testid="guide-progress">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground font-medium">Your Progress</span>
              <span className="text-sm text-[#00A6A0] font-bold">{doneSteps}/{totalSteps} steps ({progressPercent}%)</span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-[#00A6A0] rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }} />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {guide.sections.map((section, sIdx) => {
            const SectionIcon = section.icon;
            const isOpen = expandedSections[sIdx];
            const sectionDone = section.steps.every((_, stIdx) => completedSteps[`${sIdx}-${stIdx}`]);
            return (
              <Card key={sIdx} className={`glass-card border ${sectionDone ? 'border-[#00A6A0]/30' : 'border-border'}`}
                data-testid={`guide-section-${sIdx}`}>
                <button
                  onClick={() => toggleSection(sIdx)}
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${sectionDone ? 'bg-[#00A6A0]/20' : 'bg-muted'}`}>
                      <SectionIcon className={`w-5 h-5 ${sectionDone ? 'text-[#00A6A0]' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{section.title}</p>
                      <p className="text-[10px] text-muted-foreground">{section.steps.length} steps</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sectionDone && <CheckCircle2 className="w-4 h-4 text-[#00A6A0]" />}
                    {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>
                {isOpen && (
                  <CardContent className="pt-0 pb-4 px-4">
                    <div className="space-y-2 ml-2 border-l-2 border-border pl-4">
                      {section.steps.map((step, stIdx) => {
                        const key = `${sIdx}-${stIdx}`;
                        const done = completedSteps[key];
                        return (
                          <div key={stIdx} className={`p-3 rounded-lg transition-all ${done ? 'bg-[#00A6A0]/5' : 'bg-muted/50'}`}
                            data-testid={`guide-step-${sIdx}-${stIdx}`}>
                            <div className="flex items-start gap-3">
                              <button onClick={() => toggleStep(sIdx, stIdx)} className="flex-shrink-0 mt-0.5">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                  done ? 'border-[#00A6A0] bg-[#00A6A0]' : 'border-muted-foreground'
                                }`}>
                                  {done && <CheckCircle2 className="w-3 h-3 text-white" />}
                                </div>
                              </button>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className={`text-sm font-medium ${done ? 'text-[#00A6A0]' : 'text-foreground'}`}>{step.title}</p>
                                  {step.highlight && (
                                    <Button size="sm" variant="ghost"
                                      className="h-6 px-2 text-[10px] text-[#00A6A0] hover:bg-[#00A6A0]/10 gap-1"
                                      onClick={(e) => { e.stopPropagation(); startSpotlight(sIdx, stIdx); }}
                                      data-testid={`try-it-${sIdx}-${stIdx}`}>
                                      <Play className="w-3 h-3" /> Try it
                                    </Button>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.text}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
